import { sessionPauseRecording } from '@/lib/analytics/scribe/session';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  cancelRecordingControlAttempt,
  finishRecordingControlAttempt,
  setRecordingControlTransport,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import {
  CHRONICLE_ORIGIN,
  chronicleSessionIdAtom,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { type Dispatch } from '@/store';
import { chronicleBridge } from '@/tauri/chronicle';
import {
  trackHardwareRecordingPause,
  trackHardwareRecordingPauseFailed,
} from '@/utils/analytics/track-hardware-events';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

/**
 * Closes the attempt a desktop press (click or shortcut) opened. A device-side
 * pause has no press, so it carries none.
 */
function finishPauseAttempt(
  tracking: RecordingControlTracking | undefined,
  error?: unknown
): void {
  if (error !== undefined) {
    finishRecordingControlAttempt(tracking, { outcome: 'failed', error });
  } else {
    finishRecordingControlAttempt(tracking);
  }
}

/**
 * Core hook for pausing a Chronicle recording session.
 *
 * Handles both desktop-initiated and device-initiated pauses:
 *
 * **Desktop-initiated** (user clicks pause on desktop):
 * - `useRecordingButton` has already updated query cache + PATCHed backend
 *   with `recording_status: PAUSED` before this callback fires.
 * - This hook sends the BLE command to the device, pauses the timer, and
 *   tracks analytics.
 *
 * **Device-initiated** (user presses pause on Chronicle device):
 * - `ChronicleStateProvider` updates `chronicleDeviceRecordingInfoAtom`
 *   with `isPaused: true`, which `useChronicleSessionControl` detects.
 * - This hook updates the query cache + PATCHes backend (since
 *   `useRecordingButton` is NOT involved), pauses the timer, and tracks
 *   analytics. Does NOT send BLE command (device already paused).
 *
 * **Guards**:
 * - `chronicleSessionIdAtom` must be set (active session).
 * - `isPausingRef` is per hook instance, unlike the stop hook's module-level
 *   guard: only a press re-entering this instance joins its in-flight pause
 *   and takes its outcome; one entering another instance sends its own BLE
 *   pause. A device pause never awaits, so there is none in flight to join.
 *
 * Timer: calls `timerModel.onPause()` which keeps the current seconds
 * value but stops the interval. On resume, `onStart(currentSeconds)` will
 * restart from the paused position.
 */
export function useChroniclePauseRecording() {
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const isPausingRef = useRef(false);
  // The attempt waiting on the in-flight pause: the newest press's.
  const pendingAttemptRef = useRef<RecordingControlTracking | undefined>(undefined);
  const settlePendingAttempt = useCallback((error?: unknown) => {
    const tracking = pendingAttemptRef.current;
    pendingAttemptRef.current = undefined;
    finishPauseAttempt(tracking, error);
  }, []);

  const pauseChronicleRecording = useCallback(
    async (args: {
      origin: ChronicleRecordingOrigin;
      tracking?: RecordingControlTracking;
    }) => {
      setRecordingControlTransport(args.tracking, 'remote_device');
      if (!chronicleSessionId) {
        logger.warn(
          '[useChroniclePauseRecording] No active Chronicle session, skipping pause'
        );
        // The device never got the pause.
        finishPauseAttempt(
          args.tracking,
          new DesktopCaptureError(
            DesktopCaptureFailure.RECORDER_STOP_FAILED,
            'No active Chronicle session to pause'
          )
        );
        return;
      }
      if (isPausingRef.current) {
        logger.warn(
          '[useChroniclePauseRecording] Already pausing, joining the in-flight pause'
        );
        // Join the in-flight pause: its result closes this attempt. Opening
        // the handle superseded any earlier one, so keep only it.
        if (args.tracking) pendingAttemptRef.current = args.tracking;
        return;
      }
      isPausingRef.current = true;
      pendingAttemptRef.current = args.tracking;

      const sessionId = chronicleSessionId;

      try {
        // 1. Desktop-initiated: send BLE command to pause device.
        //    Device-initiated: device already paused — skip bridge call.
        if (args.origin === CHRONICLE_ORIGIN.DESKTOP) {
          try {
            await chronicleBridge.pauseRecording();
            // Device acked: capture stopped.
            settlePendingAttempt();
          } catch (error) {
            // The device did not pause, though the UI continues. Settle first,
            // so a throwing logger cannot turn this failure into a success.
            settlePendingAttempt(error);
            logger.error(
              '[useChroniclePauseRecording] chronicleBridge.pauseRecording failed',
              {
                errorName: error instanceof Error ? error.name : typeof error,
              }
            );
            trackHardwareRecordingPauseFailed({
              sessionIdLocal: sessionId,
              origin: args.origin,
              errorType: error instanceof Error ? error.name : 'UNKNOWN',
              errorDescription: error instanceof Error ? error.message : String(error),
            });
            // The product events below still fire: they describe the UI
            // transition, which did happen, while the contract attempt
            // describes the device, which did not. The two dashboards disagree
            // on this path by design.
            // Continue — UI state should still reflect paused
          }
        }

        // 2. Device-initiated: update query cache + PATCH backend.
        //    Desktop-initiated: already handled by useRecordingButton → skip.
        if (args.origin === CHRONICLE_ORIGIN.DEVICE) {
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => {
              if (!old) return undefined;
              return { ...old, recording_status: RecordingStatus.PAUSED };
            }
          );

          void updateSessionById(sessionId, {
            recording_status: RecordingStatus.PAUSED,
          }).catch((err: unknown) => {
            logger.warn(
              '[useChroniclePauseRecording] Failed to PATCH recording_status=PAUSED',
              { errorName: err instanceof Error ? err.name : typeof err }
            );
          });
        }

        // 3. Pause timer — keeps current seconds, stops interval
        dispatch.timerModel.onPause();

        // 4. Track analytics
        sessionPauseRecording({
          sessionId,
        });
        trackHardwareRecordingPause({
          sessionId,
          sessionIdLocal: sessionId,
          origin: args.origin,
        });

        logger.debug('[useChroniclePauseRecording] Recording paused', {
          sessionId,
          origin: args.origin,
        });
      } finally {
        isPausingRef.current = false;
        // Safety net: no attempt outlives the pause it joined. The bridge
        // result settles it first, so a leftover has no outcome to report.
        cancelRecordingControlAttempt(pendingAttemptRef.current);
        pendingAttemptRef.current = undefined;
      }
    },
    [chronicleSessionId, dispatch, queryClient, settlePendingAttempt]
  );

  return { pauseChronicleRecording };
}
